import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BundleAdminDetailToken, BundleAdminListItemToken, BundleAdminSaveRequest } from '@sleed/bizcommerce-api';
import { bundlesApi } from '~/lib/api/client';

export interface BundlesListFilter {
	/** 3 Bundle, 4 Giftbox; omitted = both. */
	type?: number;
	isActive?: boolean;
	containsProductId?: number;
	/** false = only Bundle/Giftbox products without contents (the "New bundle" candidates); omitted = all. */
	hasContents?: boolean;
	/** Server paging: rows to skip. */
	skip?: number;
	/** Server paging: page size; omitted = every match. */
	take?: number;
}

/** One server page of the bundles list; `total` counts every match, not just this page. */
export interface BundlesListPage {
	items: BundleAdminListItemToken[];
	total: number;
}

export const bundleKeys = {
	all: ['bundles'] as const,
	list: (filter: BundlesListFilter) => [...bundleKeys.all, 'list', filter] as const,
	detail: (id: number) => [...bundleKeys.all, 'detail', id] as const
};

/** One row per Bundle/Giftbox product, with totals and checkout status, paged on the server. Server role: Catalog. */
export function useBundlesList(filter: BundlesListFilter = {}, enabled = true) {
	return useQuery<BundlesListPage>({
		queryKey: bundleKeys.list(filter),
		queryFn: async () => {
			const { type, isActive, containsProductId, hasContents, skip, take } = filter;
			const data = (await bundlesApi.getAll(type, isActive, containsProductId, hasContents, skip, take)).data;
			const items = data.items ?? [];
			return { items, total: data.total ?? items.length };
		},
		// Paged grid: keep the current page on screen while the next page/filter loads.
		placeholderData: filter.take != null ? keepPreviousData : undefined,
		enabled
	});
}

/** A bundle's contents by its product id (404 when the product isn't a Bundle/Giftbox — no retry). */
export function useBundle(id: number | undefined, enabled = true) {
	const valid = typeof id === 'number' && Number.isFinite(id) && id > 0;
	return useQuery<BundleAdminDetailToken>({
		queryKey: bundleKeys.detail(valid ? id : 0),
		queryFn: async () => (await bundlesApi.getById(id)).data,
		enabled: enabled && valid,
		retry: false
	});
}

/** Replaces the whole contents in one call; seeds the detail cache from the response and refreshes the lists. */
export function useSaveBundle() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (request: BundleAdminSaveRequest) => (await bundlesApi.save(request)).data,
		onSuccess: (saved, request) => {
			if (request.bid) qc.setQueryData(bundleKeys.detail(request.bid), saved);
			void qc.invalidateQueries({ queryKey: [...bundleKeys.all, 'list'] });
		}
	});
}
